import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { UserRole } from 'src/graphql/generated/graphql.generated';
import { PermissionsService } from 'src/lib/amg-authorization/permissions.service';
import { patchTelemetryProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { UserViewModel } from 'src/models/person-view-model';
import { AppState } from 'src/store/appstate';
import { selectCompanyById } from 'src/store/entityStores/company-selectors';
import {
  selectPersonByEmail,
  selectPersonByEmployeeId,
} from 'src/store/entityStores/person-selectors';
import { selectAccessibleItemEntitiesByPersonId } from 'src/store/generated/accessible-item-record.generated';
import { PersonEntityFetchService } from 'src/store/generated/client-sync.generated';
import { PersonProps } from 'src/store/generated/person-record.generated';
import { StoreService } from 'src/store/store.service';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { getTrailingDigits } from 'src/util/string';
import { isEqual } from 'lodash-es';
import { TcsPermission } from '../../app/shared/authorization/permissions';
import { AppInsightsService } from '../tcs-app-insights/app-insights-initializer.service';
import { NewRelicService } from '../tcs-new-relic/new-relic-initializer.service';
import { AuthenticationService } from './authentication.service';
import { ImpersonationService } from './impersonation.service';
import { UserEmailService } from './user-email.service';
import { UserDataContextService } from './user-data-context.service';

interface IUserService {
  /**
   * The authenticated identity
   */
  readonly loggedInUser$: Observable<UserViewModel | undefined>;

  /**
   * The identity of the user being impersonated
   */
  readonly impersonatedUser$: Observable<UserViewModel | undefined>;

  /**
   * The identity of the current user - either the impersonated identity if a user is being impersonated or the current authenticated identity
   */
  readonly user$: Observable<UserViewModel | undefined>;

  /**
   * The current value of user$
   */
  readonly user: UserViewModel | undefined;

  hasPermissionAnyScope(permission: TcsPermission): Observable<boolean>;
  hasGlobalRole(role: UserRole): Observable<boolean>;
}

function getUserViewModel(person$: Observable<PersonProps | undefined>, store: Store<AppState>) {
  const company$ = person$.pipe(
    undefinedOrSwitchMap((p) => store.select(selectCompanyById(p.companyId))),
    filterNull(),
  );
  const authUser$ = person$.pipe(
    withLatestFrom(company$),
    switchMap(([p, c]) => {
      if (p == null) {
        return of(undefined);
      }
      return store
        .select(selectAccessibleItemEntitiesByPersonId(p.id))
        .pipe(map((ai) => new UserViewModel(p, c.companyCode, ai.toArray())));
    }),
  );
  return authUser$;
}

@Injectable({ providedIn: 'root' })
export class UserService implements IUserService, OnDestroy {
  private _loggedInUser$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _impersonatedUser$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _user$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _user: UserViewModel | undefined;
  private _onDestroy$ = new Subject();

  /** Only updates when the user id changes */
  get distinctUser$() {
    return this._user$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id));
  }

  get user(): UserViewModel | undefined {
    return this._user;
  }

  get loggedInUser$(): Observable<UserViewModel | undefined> {
    return this._loggedInUser$.asObservable();
  }

  get impersonatedUser$(): Observable<UserViewModel | undefined> {
    return this._impersonatedUser$.asObservable();
  }

  get user$(): Observable<UserViewModel | undefined> {
    return this._user$.asObservable();
  }

  get userId$(): Observable<string | undefined> {
    return this.distinctUser$.pipe(
      map((u) => u?.id),
      distinctUntilChanged(),
    );
  }

  get userDetails$() {
    return this.user$.pipe(
      map((user) => {
        if (user != null) {
          return {
            name: `${user.firstName} ${user.lastName}`,
            emailaddress: user.email == null ? 'Unknown' : user.email,
            companyCode: user.companyCode,
          };
        }
      }),
    );
  }

  public loggedInUserHasPermissionAnyScope(permission: TcsPermission): Observable<boolean> {
    return this.loggedInUser$.pipe(
      map((u) => u != null && u.hasPermissionAnyScope(this._permissionsService, permission)),
    );
  }

  public hasAccessibleCompany(companyId: string) {
    return this.user$.pipe(map((u) => u != null && u.hasAccessibleCompany(companyId)));
  }

  public hasPermissionAnyGlobalRole(permission: TcsPermission) {
    return this.user$.pipe(
      map((u) => u != null && u.hasPermissionAnyGlobalRole(this._permissionsService, permission)),
    );
  }

  public hasPermissionAnyScope(permission: TcsPermission) {
    return this.user$.pipe(
      map((u) => u != null && u.hasPermissionAnyScope(this._permissionsService, permission)),
    );
  }

  public hasRoleAnyScope(role: UserRole) {
    return this.user$.pipe(map((u) => u != null && u.hasRoleAnyScope(role)));
  }

  public hasAnyRoleAnyScope(roles: UserRole[]) {
    return this.user$.pipe(map((u) => u != null && u.hasAnyRoleAnyScope(roles)));
  }

  public hasGlobalRole(role: UserRole) {
    return this.user$.pipe(map((u) => u != null && u.hasGlobalRole(role)));
  }

  public hasAnyGlobalRole(roles: UserRole[]) {
    return this.user$.pipe(map((u) => u != null && u.hasAnyGlobalRole(roles)));
  }

  public isImpersonating() {
    return combineLatest([this.loggedInUser$, this.user$]).pipe(
      map(([loggedInUser, user]) => {
        if (user == null || loggedInUser == null) {
          return false;
        }

        return user.id !== loggedInUser.id;
      }),
    );
  }

  constructor(
    private _permissionsService: PermissionsService,
    private _impersonation: ImpersonationService,
    private _store: StoreService,
    private _auth: AuthenticationService,
    private _personFetch: PersonEntityFetchService,
    private _userEmailService: UserEmailService,
    private _appInsightsService: AppInsightsService,
    private _newRelicService: NewRelicService,
    private _userDataContext: UserDataContextService,
  ) {
    const authenticated$ = combineLatest([this._auth.idClaims$, this._auth.accessClaims$]).pipe(
      debounceTime(10),
      map(([, accessToken]) => (accessToken == null ? undefined : accessToken)),
      distinctUntilChanged((a1, a2) => a1?.sub === a2?.sub),
      undefinedOrSwitchMap((accessClaims) => {
        const email = accessClaims?.email;
        if (email != null) {
          // email should be set ASAP.
          this._userEmailService.setLoginEmail(email);
        }
        const employeeNumber = accessClaims?.EmployeeNumber;
        const employeeId = employeeNumber != null ? getTrailingDigits(employeeNumber) : undefined;
        const byEmployeeId$ =
          employeeId != null
            ? this._store.store.select(selectPersonByEmployeeId(employeeId))
            : of(undefined);
        const byEmail$ =
          email != null ? this._store.store.select(selectPersonByEmail(email)) : of(undefined);

        const person$ = byEmployeeId$.pipe(
          switchMap((person) => (person != null ? of(person) : byEmail$)),
          map((person) => person?.id),
          distinctUntilChanged(),
          undefinedOrSwitchMap((personId) => this._personFetch.getById(personId)),
          switchMap(async (person) => {
            if (person != null) {
              // sync user data prior to creating the user view model
              await this._userDataContext.syncUserData();
            }
            return person;
          }),
          share(),
        );
        return getUserViewModel(person$, this._store.store);
      }),
      distinctUntilChanged((a, b) => isEqual(a?.toJS(), b?.toJS())),
      share(),
    );
    authenticated$.pipe(takeUntil(this._onDestroy$)).subscribe(this._loggedInUser$);

    const impersonated$ = combineLatest([
      this._impersonation.impersonatedUserId$.pipe(distinctUntilChanged()),
      authenticated$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)),
    ]).pipe(
      debounceTime(10),
      map(([impId, authn]) => (impId != null && impId !== authn?.id ? impId : undefined)),
      undefinedOrSwitchMap((impId) => {
        const person$ = this._personFetch.getById(impId).pipe(
          switchMap(async (person) => {
            if (person != null) {
              // sync user data prior to creating the user view model
              await this._userDataContext.syncUserData();
            }
            return person;
          }),
          share(),
        );
        return getUserViewModel(person$, _store.store);
      }),
      distinctUntilChanged((a, b) => isEqual(a?.toJS(), b?.toJS())),
      share(),
    );

    impersonated$.pipe(takeUntil(this._onDestroy$)).subscribe((imp) => {
      this._impersonatedUser$.next(imp);
    });

    const user$ = combineLatest([impersonated$, authenticated$]).pipe(
      filter(([imp, auth]) => (imp == null && auth != null) || (imp != null && auth != null)),
      debounceTime(10),
      map(([imp, auth]) => imp ?? auth),
      distinctUntilChanged((a, b) => isEqual(a?.toJS(), b?.toJS())),
      share(),
    );

    // emit each time data for the authenticated or impersonated user changes
    user$.pipe(takeUntil(this._onDestroy$)).subscribe((u) => {
      this._user = u;
      this._user$.next(u);
    });

    // emit once per user id change
    user$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        filterNull(),
        takeUntil(this._onDestroy$),
      )
      .subscribe((u) => {
        this._userEmailService.setLatestEmail(u.email);
      });

    this._appInsightsService.intialized$
      .pipe(
        switchMap(() => this.user$),
        map((u) => u?.email),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$),
      )
      .subscribe((userEmail) => {
        if (userEmail != null) {
          this._appInsightsService.appInsights?.setAuthenticatedUserContext(userEmail);
        } else {
          this._appInsightsService.appInsights?.clearAuthenticatedUserContext();
        }
      });

    // New Relic - Set User Info
    this._newRelicService.intialized$
      .pipe(
        switchMap(() => this.user$),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$),
      )
      .subscribe((user) => {
        if (user != null) {
          this._newRelicService.newRelic?.setUserId(user.email);
          this._newRelicService.newRelic?.setCustomAttribute('userCompanyCode', user.companyCode);
        } else {
          this._newRelicService.newRelic?.setUserId(null);
          this._newRelicService.newRelic?.setCustomAttribute('userCompanyCode', null);
        }
      });

    this._appInsightsService.intialized$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this._appInsightsService.appInsights?.addTelemetryInitializer((item) => {
        try {
          if (this.user != null) {
            patchTelemetryProps(item, {
              userId: this.user.email,
              user: this.user,
              userCompanyCode: this.user.companyCode,
              userEmail: this.user.email,
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize telemetry with user data', err);
        }
      });

      this._appInsightsService.appInsights?.addTelemetryInitializer((item) => {
        try {
          if (this.user != null) {
            patchTelemetryProps(item, {
              userId: this.user.email,
              user: this.user,
              userCompanyCode: this.user.companyCode,
              userEmail: this.user.email,
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize telemetry with user data', err);
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
